import { useEffect } from 'react';
import classNames from 'classnames';
import { t } from 'ttag';

import { Modal, ModalProps } from '@ui/Modal';
import { Title } from '@ui/Title';
import { useBasket } from '@hook/useBasket';
import { sendRTBHouseCartStatusEvent } from '@utils/analytics';
import {
  CartSellerProductsGroup,
  CartSellerProductsGroupPreloader,
} from '@components/CartSellerProductsGroup';
import { Button } from '@ui/Button';
import BasketIcon from '@svg/basket.svg';
import { mainPage } from '@legacyUrls';
import { LocalizedLink } from '@ui/LocalizedLink';

import s from './CartModal.sss';

type CartModalProps = ModalProps & {
  className?: string
};

export const CartModal: React.FC<CartModalProps> = ({
  className,
  onRequestClose,
  ...props
}) => {
  const {
    basket,
    loading,
    deleteProduct,
    updateCount,
  } = useBasket({ lazy: true });

  useEffect(() => {
    if (basket?.groups.length) {
      const productsIds = basket.groups.reduce<string[]>((acc, item) => (
        acc.concat(item.basketProducts.map((product) => String(product.product?.id)))
      ), []);
      sendRTBHouseCartStatusEvent(productsIds);
    }
  }, [basket]);

  let content;
  if (loading && !basket) {
    // Initial loading
    content = (
      <>
        <CartSellerProductsGroupPreloader
          className={s.sellerGroup}
          productsCount={2}
        />
        <CartSellerProductsGroupPreloader className={s.sellerGroup} />
      </>
    );
  } else if (basket?.groups.length) {
    // Products in basket
    content = basket.groups.map(
      ({
        seller,
        info,
        basketProducts,
      }, i) => (
        <CartSellerProductsGroup
          key={i}
          className={s.sellerGroup}
          seller={seller!}
          info={info}
          basketProducts={basketProducts || []}
          onRequestRemoveProduct={
           (id) => deleteProduct({
             variables: { id },
           })
         }
          onUpdateBasketProductCount={
           (basketProductId, count) => updateCount({
             basketProductId,
             count,
           })
         }
        />
      ),
    );
  } else {
    // Emtpy state
    content = (
      <div className={s.emptyState}>
        <BasketIcon
          className={s.basketIcon}
          width='48'
          height='48'
        />
        <Title
          className={s.emptyStateTitle}
          type="h3"
        >
          {t`Кошик пустий`}
        </Title>
        <div className={s.emptyStateDescription}>
          {t`Перегляньте наші новинки та поновіть свій гардероб`}
        </div>

        <LocalizedLink
          theme="clean"
          href={mainPage}
        >
          <Button className={s.toPurchasesButton}>
            {t`До покупок`}
          </Button>
        </LocalizedLink>
      </div>
    );
  }

  return (
    (
      <Modal
        className={classNames(s.root, className)}
        withClose={true}
        mFullScreen={true}
        onRequestClose={onRequestClose}
        {...props}
      >
        <Title type="h4">{t`Кошик`}</Title>
        <div className={s.content}>
          {content}
        </div>
      </Modal>
    )
  );
};
